import { AuditPriority, ExceptionSeverity } from '@/types/asset-audit';
import { cn } from '@/lib/utils';
import { AlertCircle, AlertTriangle, Info, Flame } from 'lucide-react';

interface PriorityIndicatorProps {
  readonly priority: AuditPriority | ExceptionSeverity;
  readonly showLabel?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly className?: string;
}

const auditPriorityConfig: Record<
  AuditPriority,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  [AuditPriority.Low]: {
    label: 'Low',
    icon: Info,
    color: 'text-blue-600',
  },
  [AuditPriority.Medium]: {
    label: 'Medium',
    icon: AlertCircle,
    color: 'text-amber-600',
  },
  [AuditPriority.High]: {
    label: 'High',
    icon: AlertTriangle,
    color: 'text-orange-600',
  },
  [AuditPriority.Critical]: {
    label: 'Critical',
    icon: Flame,
    color: 'text-red-600',
  },
};

const exceptionSeverityConfig: Record<
  ExceptionSeverity,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  [ExceptionSeverity.Low]: {
    label: 'Low',
    icon: Info,
    color: 'text-blue-600',
  },
  [ExceptionSeverity.Medium]: {
    label: 'Medium',
    icon: AlertCircle,
    color: 'text-amber-600',
  },
  [ExceptionSeverity.High]: {
    label: 'High',
    icon: AlertTriangle,
    color: 'text-orange-600',
  },
  [ExceptionSeverity.Critical]: {
    label: 'Critical',
    icon: Flame,
    color: 'text-red-600',
  },
};

const sizeConfig = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export function PriorityIndicator({
  priority,
  showLabel = false,
  size = 'md',
  className,
}: Readonly<PriorityIndicatorProps>) {
  // Determine if it's an audit priority or exception severity
  const isAuditPriority = Object.values(AuditPriority).includes(priority as AuditPriority);
  const config = isAuditPriority
    ? auditPriorityConfig[priority as AuditPriority]
    : exceptionSeverityConfig[priority as ExceptionSeverity];

  const Icon = config.icon;

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Icon className={cn(sizeConfig[size], config.color)} />
      {showLabel && (
        <span className={cn('text-sm font-medium', config.color)}>
          {config.label}
        </span>
      )}
    </div>
  );
}
